import React, { useRef } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, Dialog, Typography } from '@material-ui/core';
import VehicleCheckResult from './VehicleCheckResult';
import VehicleCheckIcon from './VehicleCheckIcon';
import { MAX_IMAGES } from './useVehicleCheck';
import tokens from '../theme/tokens';

// 확인 모달 — "물품이 어떤 건가요? 간편하게 조회해보세요" · 물품 설명 + 제품 사진(최대 3장) → [AI로 확인하기] → 결과
const useStyles = makeStyles((theme) => ({
  paper: {
    width: 560,
    maxWidth: 'calc(100% - 32px)',
    padding: '30px 30px 26px',
    borderRadius: tokens.radiusLg,
    boxShadow: tokens.dialogShadow,
    [theme.breakpoints.down('sm')]: { padding: '26px 18px 22px' },
  },
  head: { display: 'flex', alignItems: 'flex-start' },
  headIcon: { flex: 'none', marginRight: 12, marginTop: -4 },
  titles: { flex: '1 1 auto', minWidth: 0 },
  title: { fontSize: 22, fontWeight: 700, letterSpacing: -1, color: tokens.ink, lineHeight: 1.25 },
  sub: { marginTop: 4, fontSize: 14, letterSpacing: -0.5, color: tokens.muted },
  close: {
    flex: 'none',
    width: 34,
    height: 34,
    marginTop: -4,
    marginRight: -8,
    borderRadius: 17,
    color: tokens.muted,
    '&:hover': { color: tokens.primaryDark, backgroundColor: tokens.primarySoft },
  },
  icon: { width: 18, height: 18 },
  textarea: {
    width: '100%',
    minHeight: 96,
    marginTop: 18,
    padding: '14px 16px',
    borderRadius: tokens.radius,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    fontSize: 15,
    fontFamily: 'inherit',
    lineHeight: 1.55,
    letterSpacing: -0.4,
    color: tokens.ink,
    resize: 'vertical',
    outline: 'none',
    transition: 'border-color .12s, box-shadow .12s',
    '&:focus': { borderColor: tokens.primary, boxShadow: tokens.shadow },
    '&::placeholder': { color: tokens.placeholder },
  },
  row: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' },
  thumbs: { display: 'flex', gap: 8 },
  thumb: {
    position: 'relative', width: 56, height: 56, borderRadius: 10, overflow: 'hidden', border: `1px solid ${tokens.line}`,
    '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  },
  thumbDel: {
    position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: 9, backgroundColor: 'rgba(22,22,23,0.6)', color: tokens.surface, fontSize: 11, lineHeight: '18px',
  },
  pick: {
    height: 40, padding: '0 14px', borderRadius: 20, border: `1px solid ${tokens.line}`, backgroundColor: tokens.surface, fontSize: 14, fontWeight: 700, color: tokens.text,
    '&:hover': { borderColor: tokens.primary, color: tokens.primaryDark },
  },
  run: {
    marginLeft: 'auto', height: 44, padding: '0 22px', borderRadius: 22, backgroundColor: tokens.primary, color: tokens.surface, fontSize: 15, fontWeight: 700, letterSpacing: -0.6,
    transition: 'background-color .12s',
    '&:hover': { backgroundColor: tokens.primaryDark },
    '&:disabled': { backgroundColor: tokens.line },
  },
  error: { marginTop: 10, fontSize: 13, color: tokens.primaryDark },
  again: { marginTop: 14, fontSize: 13, fontWeight: 700, color: tokens.primaryDark },
}));

function VehicleCheckDialog({ open, onClose, onConsult, check }) {
  const cs = useStyles();
  const fileRef = useRef(null);
  const { text, setText, files, addFiles, removeFile, busy, result, error, run, reset, canRun } = check;

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: cs.paper }} aria-labelledby="vehicle-check-title">
      <div className={cs.head}>
        <VehicleCheckIcon className={cs.headIcon} size={52} />
        <div className={cs.titles}>
          <Typography id="vehicle-check-title" className={cs.title}>물품이 어떤 건가요?</Typography>
          <Typography className={cs.sub}>간편하게 조회해보세요</Typography>
        </div>
        <ButtonBase className={cs.close} onClick={onClose} aria-label="닫기" focusRipple={false}>
          <svg className={cs.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </ButtonBase>
      </div>

      <textarea
        className={cs.textarea}
        value={text}
        maxLength={1000}
        placeholder="예) 모니터 박스 3개, 각 60×40×50cm, 깨지기 쉬움"
        onChange={(e) => setText(e.target.value)}
        aria-label="물품 설명"
      />
      <div className={cs.row}>
        <div className={cs.thumbs}>
          {files.map((f, i) => (
            <div key={f.preview} className={cs.thumb}>
              <img src={f.preview} alt="" />
              <ButtonBase className={cs.thumbDel} onClick={() => removeFile(i)} aria-label="사진 삭제" focusRipple={false}>×</ButtonBase>
            </div>
          ))}
        </div>
        {files.length < MAX_IMAGES && (
          <ButtonBase className={cs.pick} onClick={() => fileRef.current && fileRef.current.click()} focusRipple={false}>제품 사진 {files.length ? '추가' : '올리기'}</ButtonBase>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        <ButtonBase className={cs.run} onClick={run} disabled={!canRun} focusRipple={false}>{busy ? '확인 중' : 'AI로 확인하기'}</ButtonBase>
      </div>
      {error && <div className={cs.error}>{error}</div>}

      <VehicleCheckResult result={result} compare={check.compare} compareBusy={check.compareBusy} hasRoute={check.hasRoute} onConsult={onConsult} />
      {result && <ButtonBase className={cs.again} onClick={reset} focusRipple={false}>다른 물품 확인하기</ButtonBase>}
    </Dialog>
  );
}

export default VehicleCheckDialog;
